import React, { useState, useEffect, useCallback } from 'react';
import { Col, Dropdown, DropdownMenu, DropdownToggle, Nav, NavItem, NavLink, Row, TabContent, TabPane, Button } from 'reactstrap';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import SimpleBar from 'simplebar-react';
import io from 'socket.io-client';
import { toast, ToastContainer } from 'react-toastify';
import bell from '../../assets/images/svg/bell.png';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { handleApiError } from '../../helpers/api';
import { useDispatch } from 'react-redux';

// Singleton Socket.IO instance
const socket = io('https://api.confidoo.com.tn', {
    withCredentials: true,
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
});

const NotificationDropdown = ({ userId, role }) => {
    const { t } = useTranslation();
    const [isNotificationDropdown, setIsNotificationDropdown] = useState(false);
    const [activeTab, setActiveTab] = useState('1');
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const toggleNotificationDropdown = () => {
        setIsNotificationDropdown(!isNotificationDropdown);
    };

    const toggleTab = (tab) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
        }
    };

    // Fetch notifications
    const fetchNotifications = useCallback(async () => {
        if (!userId || !role) return;

        try {
            const response = await api.get(`/notifications/${userId}`, {
                withCredentials: true,
            });
            const data = response.data.data || response.data;
            console.log('Fetched notifications:', data);
            setNotifications((prev) => {
                // Replace temp IDs with backend IDs
                const updatedNotifications = data.map((notification) => {
                    const existing = prev.find(
                        (n) =>
                            n._id.startsWith('temp-') &&
                            n.message === notification.message &&
                            n.createdAt === notification.createdAt
                    );
                    return existing ? { ...notification, _id: notification._id } : notification;
                });
                setUnreadCount(updatedNotifications.filter((n) => n.status === 'unread' || n.status === 'new').length);
                return updatedNotifications;
            });
        } catch (err) {
            // toast.error(t('notifications.toasts.fetch_failed'), { toastId: `fetch-error-${Date.now()}` });
            // await handleApiError(err, dispatch, navigate);
        }
    }, [userId, role, dispatch, navigate, t]);

    useEffect(() => {
        fetchNotifications();
        // Periodic sync to replace temp IDs
        const interval = setInterval(fetchNotifications, 30000); // Sync every 30 seconds

        // Register with Socket.IO
        console.log('Registering with Socket.IO:', { userId, role });
        socket.emit('register', { userId, role });

        // Handle new notifications
        const handleNewNotification = (notification) => {
            console.log('Received new_notification:', notification);
            if (!notification.message) {
                console.warn('Notification missing message:', notification);
                return;
            }
            const parsedNotification = {
                ...notification,
                _id: notification._id || `temp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                status: notification.status || 'unread',
                createdAt: notification.createdAt ? new Date(notification.createdAt).toISOString() : new Date().toISOString(),
            };
            setNotifications((prev) => {
                // Deduplicate by _id or message + createdAt
                const isDuplicate = prev.some(
                    (n) =>
                        n._id === parsedNotification._id ||
                        (n.message === parsedNotification.message && n.createdAt === parsedNotification.createdAt)
                );
                if (isDuplicate) {
                    console.log('Duplicate notification ignored:', parsedNotification);
                    return prev;
                }
                const updatedNotifications = [parsedNotification, ...prev];
                setUnreadCount(updatedNotifications.filter((n) => n.status === 'unread' || n.status === 'new').length);
                toast.info(notification.message, {
                    position: 'top-right',
                    autoClose: 3000,
                    toastId: `new-${parsedNotification._id}-${Date.now()}`,
                    onOpen: () => console.log('Toast displayed for:', notification.message),
                });
                return updatedNotifications;
            });
        };

        // Handle treated notifications
        const handleTreatedNotification = ({ relatedId, treatedBy }) => {
            console.log('Received treated_notification:', { relatedId, treatedBy });
            setNotifications((prev) => {
                const updatedNotifications = prev.map((n) =>
                    n.relatedId === relatedId || n.relatedId?._id === relatedId
                        ? { ...n, status: 'treated', treatedBy }
                        : n
                );
                setUnreadCount(updatedNotifications.filter((n) => n.status === 'unread' || n.status === 'new').length);
                toast.info(t('notifications.toasts.treated'), {
                    position: 'top-right',
                    autoClose: 3000,
                    toastId: `treated-${relatedId}-${Date.now()}`,
                });
                return updatedNotifications;
            });
        };

        // Debug Socket.IO connection
        socket.on('connect', () => {
            console.log('Socket.IO connected, socket ID:', socket.id);
            socket.emit('register', { userId, role });
        });
        socket.on('disconnect', () => {
            console.log('Socket.IO disconnected');
        });

        socket.on('new_notification', handleNewNotification);
        socket.on('notification_treated', handleTreatedNotification);

        return () => {
            clearInterval(interval);
            socket.off('connect');
            socket.off('disconnect');
            socket.off('new_notification', handleNewNotification);
            socket.off('notification_treated', handleTreatedNotification);
        };
    }, [userId, role, fetchNotifications, t]);

    const markAsRead = async (id) => {
        if (id.startsWith('temp-')) {
            toast.info(t('notifications.toasts.temp_id_error'), {
                toastId: `temp-warning-${id}-${Date.now()}`,
            });
            return;
        }
        try {
            await api.patch(`/notifications/${id}/read`, {}, { withCredentials: true });
            console.log(`Marking notification as read: ${id}`);
            setNotifications((prev) => {
                const updatedNotifications = prev.map((n) => (n._id === id ? { ...n, status: 'read' } : n));
                setUnreadCount(updatedNotifications.filter((n) => n.status === 'unread' || n.status === 'new').length);
                return updatedNotifications;
            });
        } catch (err) {
            if (err.response.data?.status === 401 || err.response.data?.status === 403) {
                // await handleApiError(err, dispatch, navigate);
            } else {
                toast.error(t('notifications.toasts.mark_read_failed'), {
                    toastId: `mark-read-error-${id}-${Date.now()}`,
                });
            }
        }
    };

    const markAllAsRead = async () => {
        try {
            await api.patch(`/notifications/${userId}/read-all`, {}, { withCredentials: true });
            setNotifications((prev) => {
                const updatedNotifications = prev.map((n) =>
                    n.status === 'unread' || n.status === 'new' ? { ...n, status: 'read' } : n
                );
                setUnreadCount(0);
                return updatedNotifications;
            });
        } catch (err) {
            if (err.response.data?.status === 401 || err.response.data?.status === 403) {
                // await handleApiError(err, dispatch, navigate);
            } else {
                toast.error(t('notifications.toasts.mark_all_read_failed'), {
                    toastId: `mark-all-read-error-${Date.now()}`,
                });
            }
        }
    };

    const clearAllNotifications = async () => {
        try {
            await api.delete(`/notifications/${userId}`, { withCredentials: true });
            setNotifications([]);
            setUnreadCount(0);
        } catch (err) {
            if (err.response.data?.status === 401 || err.response.data?.status === 403) {
                await handleApiError(err, dispatch, navigate);
            } else {
                toast.error(t('notifications.toasts.clear_all_failed'), {
                    toastId: `clear-all-error-${Date.now()}`,
                });
            }
        }
    };

    const getLink = (notification) => {
        switch (notification.relatedModel) {
            case 'DemandeDemo':
                return '/apps-crypto-orders';
            case 'FAQ':
                return '/apps-tickets-list';
            case 'Candidature':
                return '/apps-job-list';
            case 'InscriptionFormation':
            case 'Formation':
                return '/apps-formation-inscriptions';
            case 'Order':
                return '/apps-ecommerce-orders';
            default:
                return '#';
        }
    };

    const allNotifications = notifications;
    const messageNotifications = notifications.filter((n) =>
        ['demo_submission', 'faq_submission', 'inscription'].includes(n.eventType)
    );
    const alertNotifications = notifications.filter((n) => ['job_application', 'new_order'].includes(n.eventType));

    // Render notification item
    const renderNotification = (notification) => (
        <div
            key={notification._id}
            className={`text-reset notification-item d-block dropdown-item position-relative ${
                notification.status === 'unread' || notification.status === 'new' ? 'active' : ''
            }`}
        >
            <div className="d-flex align-items-center">
                <div className="avatar-xs me-3">
                    <span
                        className={`avatar-title d-flex align-items-center justify-content-center bg-soft-${
                            notification.eventType === 'demo_submission'
                                ? 'info'
                                : notification.eventType === 'faq_submission'
                                    ? 'warning'
                                    : notification.eventType === 'inscription'
                                        ? 'primary'
                                        : notification.eventType === 'new_order'
                                            ? 'success'
                                            : 'danger'
                        } text-${
                            notification.eventType === 'demo_submission'
                                ? 'info'
                                : notification.eventType === 'faq_submission'
                                    ? 'warning'
                                    : notification.eventType === 'inscription'
                                        ? 'primary'
                                        : notification.eventType === 'new_order'
                                            ? 'success'
                                            : 'danger'
                        } rounded-circle fs-16`}
                        style={{ paddingLeft: '5px' }}
                    >
                        <i
                            className={`bx bx-${
                                notification.eventType === 'demo_submission'
                                    ? 'badge-check'
                                    : notification.eventType === 'faq_submission'
                                        ? 'question-mark'
                                        : notification.eventType === 'inscription'
                                            ? 'calendar'
                                            : notification.eventType === 'new_order'
                                                ? 'cart'
                                                : 'briefcase'
                            }`}
                        ></i>
                    </span>
                </div>
                <div className="flex-1">
                    <div className="notification-content">
                        <Link to={getLink(notification)}>
                            <h6 className="mt-0 mb-2 lh-base">
                                {notification.message}
                                {notification.status === 'treated' && notification.treatedBy && (
                                    <span className="text-muted">
                                        {' '}
                                        ({t('notifications.treated_by')} {notification.treatedBy.firstname}{' '}
                                        {notification.treatedBy.lastname})
                                    </span>
                                )}
                            </h6>
                        </Link>
                        <p className="mb-0 fs-11 fw-medium text-uppercase text-muted">
                            <span>
                                <i className="mdi mdi-clock-outline"></i>{' '}
                                {new Date(notification.createdAt).toLocaleTimeString([], {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                })}
                            </span>
                        </p>
                    </div>
                </div>
                {(notification.status === 'unread' || notification.status === 'new') && (
                    <div className="px-2 fs-15 notification-button">
                        <Button
                            color="primary"
                            size="sm"
                            onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification._id);
                            }}
                            disabled={notification._id.startsWith('temp-')}
                            title={
                                notification._id.startsWith('temp-')
                                    ? t('notifications.buttons.waiting_sync')
                                    : t('notifications.buttons.mark_as_read')
                            }
                        >
                            {t('notifications.buttons.mark_as_read')}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <React.Fragment>
            <ToastContainer
                position="top-right"
                autoClose={3000}
                limit={3}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
            <Dropdown
                isOpen={isNotificationDropdown}
                toggle={toggleNotificationDropdown}
                className="topbar-head-dropdown ms-1 header-item"
            >
                <DropdownToggle
                    type="button"
                    tag="button"
                    className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle shadow-none"
                >
                    <i className="bx bx-bell fs-22"></i>
                    {unreadCount > 0 && (
                        <span className="position-absolute topbar-badge fs-10 translate-middle badge rounded-pill bg-danger">
                            {unreadCount}
                            <span className="visually-hidden">{t('notifications.unread_messages')}</span>
                        </span>
                    )}
                </DropdownToggle>
                <DropdownMenu className="dropdown-menu-lg dropdown-menu-end p-0">
                    <div className="dropdown-head bg-primary bg-pattern rounded-top">
                        <div className="p-3">
                            <Row className="align-items-center">
                                <Col>
                                    <h6 className="m-0 fs-16 fw-semibold text-white">{t('notifications.title')}</h6>
                                </Col>
                                <div className="col-auto dropdown-tabs">
                                    <span className="badge badge-soft-light fs-13">
                                        {unreadCount} {t('notifications.new')}
                                    </span>
                                </div>
                            </Row>
                        </div>
                        <div className="px-2 pt-2">
                            <Nav className="nav-tabs dropdown-tabs nav-tabs-custom">
                                <NavItem>
                                    <NavLink
                                        href="#"
                                        className={classnames({ active: activeTab === '1' })}
                                        onClick={() => toggleTab('1')}
                                    >
                                        {t('notifications.tabs.all')} ({allNotifications.length})
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        href="#"
                                        className={classnames({ active: activeTab === '2' })}
                                        onClick={() => toggleTab('2')}
                                    >
                                        {t('notifications.tabs.messages')} ({messageNotifications.length})
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        href="#"
                                        className={classnames({ active: activeTab === '3' })}
                                        onClick={() => toggleTab('3')}
                                    >
                                        {t('notifications.tabs.alerts')} ({alertNotifications.length})
                                    </NavLink>
                                </NavItem>
                            </Nav>
                        </div>
                    </div>
                    <TabContent activeTab={activeTab}>
                        <TabPane tabId="1" className="py-2 ps-2">
                            <SimpleBar style={{ maxHeight: '300px' }} className="pe-2">
                                {allNotifications.length === 0 ? (
                                    <div className="d-flex flex-column justify-content-center align-items-center">
                                        <img
                                            src={bell}
                                            style={{ maxWidth: '30px' }}
                                            alt="no notifications"
                                            onError={() => console.error('Failed to load bell.png')}
                                        />
                                        <div className="text-center pb-5 mt-2">
                                            <h6 className="fs-18 fw-semibold lh-base">{t('notifications.no_notifications')}</h6>
                                        </div>
                                    </div>
                                ) : (
                                    allNotifications.map((notification) => renderNotification(notification))
                                )}
                                <div className="my-3 text-center">
                                    <Button color="primary" size="sm" className="me-2" onClick={markAllAsRead}>
                                        {t('notifications.buttons.mark_all_as_read')}
                                    </Button>
                                    <Button color="danger" size="sm" onClick={clearAllNotifications}>
                                        {t('notifications.buttons.clear_all')}
                                    </Button>
                                </div>
                            </SimpleBar>
                        </TabPane>
                        <TabPane tabId="2" className="py-2 ps-2">
                            <SimpleBar style={{ maxHeight: '300px' }} className="pe-2">
                                {messageNotifications.length === 0 ? (
                                    <div className="d-flex flex-column justify-content-center align-items-center">
                                        <img
                                            src={bell}
                                            style={{ maxWidth: '30px' }}
                                            alt="no notifications"
                                            onError={() => console.error('Failed to load bell.png')}
                                        />
                                        <div className="text-center pb-5 mt-2">
                                            <h6 className="fs-18 fw-semibold lh-base">{t('notifications.no_notifications')}</h6>
                                        </div>
                                    </div>
                                ) : (
                                    messageNotifications.map((notification) => renderNotification(notification))
                                )}
                                <div className="my-3 text-center">
                                    <Button color="primary" size="sm" className="me-2" onClick={markAllAsRead}>
                                        {t('notifications.buttons.mark_all_as_read')}
                                    </Button>
                                    <Button color="danger" size="sm" onClick={clearAllNotifications}>
                                        {t('notifications.buttons.clear_all')}
                                    </Button>
                                </div>
                            </SimpleBar>
                        </TabPane>
                        <TabPane tabId="3" className="py-2 ps-2">
                            <SimpleBar style={{ maxHeight: '300px' }} className="pe-2">
                                {alertNotifications.length === 0 ? (
                                    <div className="d-flex flex-column justify-content-center align-items-center">
                                        <img
                                            src={bell}
                                            style={{ maxWidth: '30px' }}
                                            alt="no notifications"
                                            onError={() => console.error('Failed to load bell.png')}
                                        />
                                        <div className="text-center pb-5 mt-2">
                                            <h6 className="fs-18 fw-semibold lh-base">{t('notifications.no_notifications')}</h6>
                                        </div>
                                    </div>
                                ) : (
                                    alertNotifications.map((notification) => renderNotification(notification))
                                )}
                                <div className="my-3 text-center">
                                    <Button color="primary" size="sm" className="me-2" onClick={markAllAsRead}>
                                        {t('notifications.buttons.mark_all_as_read')}
                                    </Button>
                                    <Button color="danger" size="sm" onClick={clearAllNotifications}>
                                        {t('notifications.buttons.clear_all')}
                                    </Button>
                                </div>
                            </SimpleBar>
                        </TabPane>
                    </TabContent>
                </DropdownMenu>
            </Dropdown>
            <style>{`
                .notification-item {
                    padding: 10px;
                    border-bottom: 1px solid #e9ecef;
                }
                .notification-content {
                    flex-grow: 1;
                }
                .notification-button {
                    position: relative;
                    z-index: 10;
                }
                .notification-item .stretched-link::after {
                    z-index: 5;
                }
            `}</style>
        </React.Fragment>
    );
};

export default NotificationDropdown;